import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ensureCsrfCookie, unwrap, uploadFile } from "../client";
import type { paths } from "../generated/schema";
import { noErrorToast, type AppMutationMeta } from "../mutationMeta";
import { requestKey } from "@/lib/requestKey";

/** The JSON body an endpoint takes (from the generated schema). */
export type Body<P extends keyof paths, M extends "post" | "patch"> =
  NonNullable<paths[P][M]> extends { requestBody?: { content: { "application/json": infer B } } } ? B : never;
/** The JSON an endpoint answers with on success. */
export type Answer<P extends keyof paths, M extends "get" | "post" | "patch"> =
  NonNullable<paths[P][M]> extends { responses: infer R }
    ? R extends { 200: { content: { "application/json": infer A } } }
      ? A
      : R extends { 201: { content: { "application/json": infer A } } }
        ? A
        : never
    : never;

/**
 * Query keys: everything about stock lives under ["tenant", slug, "inventory"], purchasing and
 * receiving under ["tenant", slug, "purchasing"], so a posting invalidates every view it changes.
 */
export const opsKeys = {
  inventory: (slug: string) => ["tenant", slug, "inventory"] as const,
  purchasing: (slug: string) => ["tenant", slug, "purchasing"] as const,
  pricing: (slug: string) => ["tenant", slug, "pricing"] as const,
  settings: (slug: string) => ["tenant", slug, "operations-settings"] as const,
};

export function useInvalidateOps(slug: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: opsKeys.inventory(slug) });
    qc.invalidateQueries({ queryKey: opsKeys.purchasing(slug) });
  };
}

const path = (slug: string) => ({ path: { tenant_slug: slug } });
const pathId = (slug: string, id: string) => ({ path: { tenant_slug: slug, id } });

// --- settings and reasons -----------------------------------------------------------------------
export type OperationsSettings = Answer<"/api/t/{tenant_slug}/operations-settings/", "get">;

export function useOperationsSettings(slug: string) {
  return useQuery({
    queryKey: opsKeys.settings(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/operations-settings/", { params: path(slug) })),
    staleTime: 60_000,
  });
}

export function useSaveOperationsSettings(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: Body<"/api/t/{tenant_slug}/operations-settings/", "patch">) =>
      unwrap(api.PATCH("/api/t/{tenant_slug}/operations-settings/", { params: path(slug), body })),
    onSuccess: (data) => qc.setQueryData(opsKeys.settings(slug), data),
  });
}

export type AdjustmentReason = Answer<"/api/t/{tenant_slug}/adjustment-reasons/", "get">[number];

export function useAdjustmentReasons(slug: string, activeOnly = true) {
  return useQuery({
    queryKey: ["tenant", slug, "adjustment-reasons", activeOnly],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/adjustment-reasons/", { params: { ...path(slug), query: activeOnly ? { active: "true" } : {} } })),
    staleTime: 60_000,
  });
}

export function useSaveReason(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: { id?: string; body: Body<"/api/t/{tenant_slug}/adjustment-reasons/", "post"> }) =>
      vars.id
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/adjustment-reasons/{id}/", { params: pathId(slug, vars.id), body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/adjustment-reasons/", { params: path(slug), body: vars.body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant", slug, "adjustment-reasons"] }),
  });
}

// --- stock --------------------------------------------------------------------------------------
export type StockFilters = { q?: string; category?: string; location?: string; held?: "true" | "false"; page?: number };
export type StockRow = Answer<"/api/t/{tenant_slug}/stock/", "get">["results"][number];
export type ItemStock = Answer<"/api/t/{tenant_slug}/stock/{id}/", "get">;
export type Balance = ItemStock["balances"][number];
export type Movement = ItemStock["movements"][number];
export type Reservation = ItemStock["reservations"][number];
export type Layer = NonNullable<ItemStock["layers"]>[number];

export function useStock(slug: string, filters: StockFilters, enabled = true) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "stock", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useItemStock(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "stock", "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock/{id}/", { params: pathId(slug, id!) })),
  });
}

export type BalanceFilters = { location?: string; subtree?: "true" | "false"; item?: string; q?: string; page?: number; page_size?: number };

export function useBalances(slug: string, filters: BalanceFilters, enabled = true) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "balances", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-balances/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export type MovementFilters = { item?: string; location?: string; kind?: string; transaction?: string; from?: string; to?: string; q?: string; page?: number };

export function useMovements(slug: string, filters: MovementFilters, enabled = true) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "movements", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-movements/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export type StockTransaction = Answer<"/api/t/{tenant_slug}/stock-transactions/{id}/", "get">;

export function useStockTransaction(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "transaction", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-transactions/{id}/", { params: pathId(slug, id!) })),
  });
}

type StockOp = "transfer" | "issue" | "return" | "adjust";
export type TransferBody = Body<"/api/t/{tenant_slug}/stock-transactions/transfer/", "post">;
export type IssueBody = Body<"/api/t/{tenant_slug}/stock-transactions/issue/", "post">;
export type ReturnBody = Body<"/api/t/{tenant_slug}/stock-transactions/return/", "post">;
export type AdjustBody = Body<"/api/t/{tenant_slug}/stock-transactions/adjust/", "post">;

/** Post a stock operation; every stock and purchasing view refreshes. Errors are shown by the caller. */
export function useStockOperation(slug: string) {
  const invalidate = useInvalidateOps(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: { op: "transfer"; body: TransferBody } | { op: "issue"; body: IssueBody } | { op: "return"; body: ReturnBody } | { op: "adjust"; body: AdjustBody }) => {
      const params = path(slug);
      switch (vars.op as StockOp) {
        case "transfer":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-transactions/transfer/", { params, body: vars.body as TransferBody }));
        case "issue":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-transactions/issue/", { params, body: vars.body as IssueBody }));
        case "return":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-transactions/return/", { params, body: vars.body as ReturnBody }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-transactions/adjust/", { params, body: vars.body as AdjustBody }));
      }
    },
    onSuccess: invalidate,
  });
}

export function useReverseTransaction(slug: string) {
  const invalidate = useInvalidateOps(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      unwrap(api.POST("/api/t/{tenant_slug}/stock-transactions/{id}/reverse/", { params: pathId(slug, id), body: { note, idempotency_key: requestKey() } })),
    onSuccess: invalidate,
  });
}

export type ReservationFilters = { status?: string; item?: string; location?: string; client?: string; q?: string; page?: number };

export function useReservations(slug: string, filters: ReservationFilters, enabled = true) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "reservations", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-reservations/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useReserve(slug: string) {
  const invalidate = useInvalidateOps(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: Body<"/api/t/{tenant_slug}/stock-reservations/", "post">) =>
      unwrap(api.POST("/api/t/{tenant_slug}/stock-reservations/", { params: path(slug), body })),
    onSuccess: invalidate,
  });
}

export function useReleaseReservation(slug: string) {
  const invalidate = useInvalidateOps(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(api.POST("/api/t/{tenant_slug}/stock-reservations/{id}/release/", { params: pathId(slug, id), body: { reason } })),
    onSuccess: invalidate,
  });
}

export function useLots(slug: string, item: string | undefined, q = "") {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "lots", item, q],
    enabled: Boolean(item),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-lots/", { params: { ...path(slug), query: { item: item!, q } } })),
  });
}

export function usePieces(slug: string, item: string | undefined, q = "") {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "pieces", item, q],
    enabled: Boolean(item),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-pieces/", { params: { ...path(slug), query: { item: item!, q } } })),
  });
}

export function useCostLayers(slug: string, filters: { item?: string; open?: "true" | "false"; page?: number }, enabled = true) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "layers", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/cost-layers/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useRevalue(slug: string) {
  const invalidate = useInvalidateOps(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: Body<"/api/t/{tenant_slug}/cost-layers/revalue/", "post">) =>
      unwrap(api.POST("/api/t/{tenant_slug}/cost-layers/revalue/", { params: path(slug), body })),
    onSuccess: invalidate,
  });
}

export function useVerifyInventory(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "verify"],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock/verify/", { params: path(slug) })),
  });
}

// --- opening stock --------------------------------------------------------------------------------
export type OpeningBatch = Answer<"/api/t/{tenant_slug}/opening-balances/{id}/", "get">;

export function useOpeningBatches(slug: string, page = 1) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "opening", "list", page],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/opening-balances/", { params: { ...path(slug), query: { page } } })),
    placeholderData: keepPreviousData,
  });
}

export function useOpeningBatch(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "opening", "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/opening-balances/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useOpeningAction(slug: string, meta: AppMutationMeta = noErrorToast) {
  const qc = useQueryClient();
  const invalidate = useInvalidateOps(slug);
  return useMutation({
    meta,
    mutationFn: async (
      vars:
        | { action: "create"; body: Body<"/api/t/{tenant_slug}/opening-balances/", "post"> }
        | { action: "update"; id: string; body: Body<"/api/t/{tenant_slug}/opening-balances/{id}/", "patch"> }
        | { action: "import"; id: string; file: File }
        | { action: "post" | "cancel"; id: string },
    ): Promise<OpeningBatch> => {
      switch (vars.action) {
        case "create":
          return unwrap(api.POST("/api/t/{tenant_slug}/opening-balances/", { params: path(slug), body: vars.body }));
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/opening-balances/{id}/", { params: pathId(slug, vars.id), body: vars.body }));
        case "import":
          return uploadFile<OpeningBatch>(`/api/t/${slug}/opening-balances/${vars.id}/import/`, vars.file);
        case "post":
          return unwrap(api.POST("/api/t/{tenant_slug}/opening-balances/{id}/post/", { params: pathId(slug, vars.id), body: { idempotency_key: requestKey() } }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/opening-balances/{id}/cancel/", { params: pathId(slug, vars.id) }));
      }
    },
    onSuccess: (batch) => {
      qc.setQueryData([...opsKeys.inventory(slug), "opening", "detail", batch.id], batch);
      invalidate();
    },
  });
}

// --- counts -------------------------------------------------------------------------------------------
export type StockCount = Answer<"/api/t/{tenant_slug}/stock-counts/{id}/", "get">;
export type CountLine = Answer<"/api/t/{tenant_slug}/stock-counts/{id}/lines/", "get">["results"][number];
export type CountLineFilter = "all" | "uncounted" | "differences" | "recount" | "stale" | "pending";

export function useCounts(slug: string, filters: { status?: string; q?: string; page?: number }) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "counts", "list", filters],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-counts/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useCount(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "counts", "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-counts/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useCountLines(slug: string, id: string | undefined, filters: { q?: string; filter?: CountLineFilter; location?: string; page?: number; page_size?: number }) {
  return useQuery({
    queryKey: [...opsKeys.inventory(slug), "counts", "lines", id, filters],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/stock-counts/{id}/lines/", { params: { ...pathId(slug, id!), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

type CountAction =
  | { action: "create"; body: Body<"/api/t/{tenant_slug}/stock-counts/", "post"> }
  | { action: "update"; id: string; body: Body<"/api/t/{tenant_slug}/stock-counts/{id}/", "patch"> }
  | { action: "start" | "refresh"; id: string }
  | { action: "submit"; id: string; allow_uncounted?: boolean }
  | { action: "cancel"; id: string; reason: string }
  | { action: "recount"; id: string; line_ids: string[]; note?: string }
  | { action: "decide"; id: string; line_ids: string[]; approve: boolean; note?: string }
  | { action: "post"; id: string }
  | { action: "add_line"; id: string; body: Body<"/api/t/{tenant_slug}/stock-counts/{id}/lines/", "post"> };

export function useCountAction(slug: string) {
  const invalidate = useInvalidateOps(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: async (vars: CountAction): Promise<unknown> => {
      const params = "id" in vars ? pathId(slug, vars.id) : undefined;
      switch (vars.action) {
        case "create":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/", { params: path(slug), body: vars.body }));
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/stock-counts/{id}/", { params: params!, body: vars.body }));
        case "start":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/start/", { params: params! }));
        case "refresh":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/refresh/", { params: params! }));
        case "submit":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/submit/", { params: params!, body: { allow_uncounted: vars.allow_uncounted } }));
        case "cancel":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/cancel/", { params: params!, body: { reason: vars.reason } }));
        case "recount":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/recount/", { params: params!, body: { line_ids: vars.line_ids, note: vars.note } }));
        case "decide":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/decide/", { params: params!, body: { line_ids: vars.line_ids, approve: vars.approve, note: vars.note } }));
        case "post":
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/post/", { params: params!, body: { idempotency_key: requestKey() } }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/stock-counts/{id}/lines/", { params: params!, body: vars.body }));
      }
    },
    onSuccess: invalidate,
  });
}

type LineAction =
  | { action: "record"; id: string; body: Body<"/api/t/{tenant_slug}/stock-count-lines/{id}/record/", "post"> }
  | { action: "confirm"; id: string; version: number }
  | { action: "explain"; id: string; body: Body<"/api/t/{tenant_slug}/stock-count-lines/{id}/explain/", "post"> };

export function useCountLineAction(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: LineAction): Promise<CountLine> => {
      const params = pathId(slug, vars.id);
      if (vars.action === "record") return unwrap(api.POST("/api/t/{tenant_slug}/stock-count-lines/{id}/record/", { params, body: vars.body }));
      if (vars.action === "confirm") return unwrap(api.POST("/api/t/{tenant_slug}/stock-count-lines/{id}/confirm/", { params, body: { version: vars.version } }));
      return unwrap(api.POST("/api/t/{tenant_slug}/stock-count-lines/{id}/explain/", { params, body: vars.body }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...opsKeys.inventory(slug), "counts"] }),
  });
}

/** Multipart upload of an attachment (packing slip, photo, evidence) to `url`. */
export async function uploadAttachment<T>(url: string, file: File, kind: string, description = "", folder = ""): Promise<T> {
  await ensureCsrfCookie();
  const { getCookie } = await import("@/lib/cookies");
  const body = new FormData();
  body.append("file", file);
  body.append("kind", kind);
  if (description) body.append("description", description);
  if (folder) body.append("folder", folder);
  const response = await fetch(url, { method: "POST", body, credentials: "same-origin", headers: { "X-CSRFToken": getCookie("kshop_csrftoken") ?? "" } });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const { toApiError } = await import("../errors");
    throw toApiError(response.status, data);
  }
  return data as T;
}

/** Download a file the API answers with (templates, reports, PDF previews), named by its Content-Disposition. */
export async function downloadGet(url: string, fallbackName: string): Promise<void> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    const { toApiError } = await import("../errors");
    throw toApiError(response.status, await response.json().catch(() => null));
  }
  const name = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
  const href = URL.createObjectURL(await response.blob());
  const link = Object.assign(document.createElement("a"), { href, download: name });
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
